import { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { NAV } from "@/config/modules";
import { canAccess } from "@/lib/rbac";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";
import { ChevronLeft, ChevronDown } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import logo from "@/assets/freshezy-logo.webp";

export function Sidebar({ collapsed, setCollapsed }) {
  const { role } = useAuth();
  const location = useLocation();

  const groups = NAV.map((g) => ({
    ...g,
    items: g.items.filter((it) => canAccess(role, it.roles)),
  })).filter((g) => g.items.length > 0);

  const activeGroup = groups.find((g) => g.items.some((it) => it.path === location.pathname))?.group;
  const [openGroups, setOpenGroups] = useState(() => {
    const init = {};
    groups.forEach((g) => { init[g.group] = true; });
    return init;
  });
  const isOpen = (name) => openGroups[name] !== false || name === activeGroup;
  const toggleGroup = (name) => setOpenGroups((s) => ({ ...s, [name]: s[name] === false ? true : false }));

  return (
    <aside
      data-testid="sidebar"
      className={cn(
        "flex h-screen flex-col bg-[#1E1B18] text-stone-300 transition-all duration-300 sticky top-0",
        collapsed ? "w-20" : "w-64"
      )}
    >
      <div className="flex items-center gap-3 border-b border-white/5 px-4 py-4">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white p-1 shadow-lg">
          <img src={logo} alt="Freshezy Foodtech Pvt Ltd" className="h-full w-full object-contain" />
        </div>
        {!collapsed && (
          <div className="min-w-0">
            <div className="font-display text-sm font-bold text-white leading-tight">Freshezy Foodtech Pvt Ltd</div>
            <div className="truncate text-[10px] text-amber-500/80">Bakery ERP · Web</div>
          </div>
        )}
      </div>

      <nav className="flex-1 overflow-y-auto px-2 py-3">
        <TooltipProvider delayDuration={0}>
          {groups.map((g) => {
            const open = collapsed || isOpen(g.group);
            return (
            <div key={g.group} className="mb-1">
              {!collapsed && (
                <button
                  data-testid={`sidebar-group-${g.group.replace(/[^a-z]+/gi, "-").toLowerCase()}`}
                  onClick={() => toggleGroup(g.group)}
                  className="flex w-full items-center justify-between px-3 pb-1 pt-3 text-[10px] font-bold uppercase tracking-wider text-amber-500/70 hover:text-amber-400"
                >
                  <span>{g.group}</span>
                  <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", !open && "-rotate-90")} />
                </button>
              )}
              {open && g.items.map((it) => {
                const Icon = it.icon;
                const active = location.pathname === it.path;
                const link = (
                  <NavLink
                    key={it.path}
                    to={it.path}
                    data-testid={`sidebar-nav-${it.testId}`}
                    className={cn(
                      "group flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                      collapsed && "justify-center",
                      active
                        ? "bg-amber-600/20 text-amber-300 shadow-[inset_2px_0_0_0] shadow-amber-500"
                        : "text-stone-400 hover:bg-white/5 hover:text-stone-100"
                    )}
                  >
                    <Icon className={cn("h-[18px] w-[18px] shrink-0", active && "text-amber-400")} />
                    {!collapsed && <span className="truncate">{it.label}</span>}
                  </NavLink>
                );
                return collapsed ? (
                  <Tooltip key={it.path}>
                    <TooltipTrigger asChild>{link}</TooltipTrigger>
                    <TooltipContent side="right" className="bg-stone-900 text-white border-stone-700">
                      {it.label}
                    </TooltipContent>
                  </Tooltip>
                ) : (
                  link
                );
              })}
            </div>
            );
          })}
        </TooltipProvider>
      </nav>

      <button
        data-testid="sidebar-collapse-toggle"
        onClick={() => setCollapsed((c) => !c)}
        className="flex items-center justify-center gap-2 border-t border-white/5 py-3 text-xs text-stone-400 hover:bg-white/5 hover:text-stone-100"
      >
        <ChevronLeft className={cn("h-4 w-4 transition-transform", collapsed && "rotate-180")} />
        {!collapsed && "Collapse"}
      </button>
    </aside>
  );
}
